import { type } from 'arktype';
import { experienceDurationTypeSchema, experienceFormatSchema } from '../experiences';
import { createPaginatedResponseSchema } from '../shared/pagination';

/**
 * Distintivo de una plantilla del arranque rápido (documento 03a, §7.3): `masSeRepite`
 * (las series con más ediciones de todo el resultado — "lo que más se repite"),
 * `recurrente` (series con **dos o más** experiencias: es la definición literal de negocio
 * del §7.3, "un plan plantilla es un formato ejecutado dos o más veces con variación
 * mínima") y `tuDiario` (series donde **quien pregunta** es el organizador de alguna de sus
 * experiencias).
 *
 * Una serie puede cumplir varios a la vez y el campo es un único valor, así que la prioridad
 * es `tuDiario` > `masSeRepite` > `recurrente` (decisión propia, `decisiones.md`: ninguna
 * fuente del cliente la fija). Consecuencia conocida: una serie de una sola edición que no
 * sea tuya no encaja en ninguno y no aparece en el arranque rápido.
 * D-21 (decisiones.md): las plantillas no se ordenan por mes — la propuesta del PDF sobre
 * estacionalidad (mes fuerte de cada plantilla, el agujero de septiembre) queda sin
 * construir en esta iteración.
 */
export const TEMPLATE_BADGES = ['masSeRepite', 'recurrente', 'tuDiario'] as const;
export const templateBadgeSchema = type.enumerated(...TEMPLATE_BADGES);
export type TemplateBadge = typeof templateBadgeSchema.infer;

/**
 * De dónde sale una plantilla (documento 03c): `deducida` la calcula el servicio de datos
 * reales de una serie (`badge`/`editionsCount`/`seriesId`/`seriesName`, ya existentes);
 * `guardada` la escribió a mano el organizador y vive en la tabla `experienceTemplate` — no
 * tiene serie detrás, así que esos cuatro campos no le aplican.
 */
export const TEMPLATE_ORIGINS = ['deducida', 'guardada'] as const;
export const templateOriginSchema = type.enumerated(...TEMPLATE_ORIGINS);
export type TemplateOrigin = typeof templateOriginSchema.infer;

/**
 * `GET /experience-templates` (documento 03a, §6.4: endpoint que faltaba — "las
 * plantillas del arranque rápido, calculadas de datos reales en vez de escritas a mano";
 * documento 03c: se amplía para devolver también las plantillas que el organizador guarda a
 * mano). Cada plantilla `deducida` sale de una `experienceSeries` real, no de un array dentro
 * de un componente (documento 03b §7.2); cada plantilla `guardada` sale de la tabla
 * `experienceTemplate`. Ruta kebab-case por la misma razón que `/experience-drafts` — ver
 * ADR-0018 y decisiones.md.
 *
 * Todo campo es opcional salvo `origen` (documento 03c): los cuatro campos de la plantilla
 * `deducida` (`badge`, `editionsCount`, `seriesId`, `seriesName`) no existen para una
 * `guardada`, y viceversa con `nombre`/`categoryId`/`format`/`durationType`/`nicheIds`/
 * `usageCount` — cada fila real solo rellena los campos de su propio origen. `id` es opcional
 * por la misma razón: una `guardada` tiene fila propia en `experienceTemplate` y lo lleva; una
 * `deducida` se calcula al vuelo desde una `experienceSeries` y no tiene fila propia, así que
 * no tiene id que exponer — el frontend lo necesita para construir `PATCH`/`DELETE
 * /experience-templates/:id` al renombrar o borrar una plantilla guardada desde el arranque
 * rápido.
 */
export const experienceTemplateResponseSchema = type({
	'badge?': templateBadgeSchema,
	'categoryId?': 'string.uuid',
	'durationType?': experienceDurationTypeSchema,
	'editionsCount?': 'number.integer >= 1',
	'format?': experienceFormatSchema,
	'id?': 'string.uuid',
	'nicheIds?': 'string.uuid[]',
	'nombre?': 'string',
	origen: templateOriginSchema,
	'seriesId?': 'string.uuid',
	'seriesName?': 'string',
	'usageCount?': 'number.integer >= 0',
}).onUndeclaredKey('delete');

export type ExperienceTemplateResponse = typeof experienceTemplateResponseSchema.infer;

export const experienceTemplatePaginatedResponseSchema = createPaginatedResponseSchema(
	experienceTemplateResponseSchema,
);

export type ExperienceTemplatePaginatedResponse = typeof experienceTemplatePaginatedResponseSchema.infer;

/**
 * `POST /experience-templates` (documento 03c): el organizador guarda una plantilla a mano,
 * distinta de las `deducidas` que ya calcula el servicio de series reales. Solo `name` es
 * obligatorio — los cuatro campos de contenido son opcionales porque una plantilla puede
 * guardarse incompleta, igual que `experienceTemplate.categoryId`/`format`/`durationType` son
 * columnas nulas en la migración.
 */
export const createExperienceTemplateSchema = type({
	'categoryId?': 'string.uuid',
	'durationType?': experienceDurationTypeSchema,
	'format?': experienceFormatSchema,
	name: 'string',
	'nicheIds?': 'string.uuid[]',
}).onUndeclaredKey('delete');

export type CreateExperienceTemplate = typeof createExperienceTemplateSchema.infer;
